/**
 * A quest beginning, and a quest finished, said where somebody will see it.
 *
 * Starting a quest used to be a line in the chat log among twenty others, which
 * is not how it feels to be given something to do. This is the moment itself:
 * the name, what it is, and the one thing worth offering on the spot, which is
 * to follow it so the world marks where to go.
 *
 * Nothing here decides anything. What is shown is worked out from what the world
 * already sent, and the only thing it can do is ask to follow, which is the same
 * button the journal has.
 */

import { useEffect, useState } from 'react';

/** How long one stays up before it takes itself away. */
const HOLD_MS = 4200;

export interface QuestNoticeEntry {
  id: string;
  name: string;
  /** A line about it: its summary when it begins, its ending when it is done. */
  about: string;
  kind: 'started' | 'finished';
  /** What it gave, in the words whoever wrote it chose. */
  rewards?: { text: string; itemId: number; quantity: number }[];
  at: number;
}

export function QuestNotice({
  notices,
  following,
  onFollow,
  onOpen,
  onDone,
  onDismiss,
}: {
  notices: readonly QuestNoticeEntry[];
  /** Which quest is being followed, so the button says the truth. */
  following: string | null;
  onFollow: (quest: string | null) => void;
  /** Opening the journal at this quest, for whoever wants the whole of it. */
  onOpen: (quest: string) => void;
  onDone: (before: number) => void;
  /** Taking one away by hand, for somebody who has read it. */
  onDismiss: (at: number) => void;
}): JSX.Element | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (notices.length === 0) return;

    const timer = window.setInterval(() => {
      const held = Date.now();
      setNow(held);
      onDone(held - HOLD_MS);
    }, 500);

    return () => window.clearInterval(timer);
  }, [notices.length, onDone]);

  const showing = notices.filter((one) => now - one.at < HOLD_MS);
  if (showing.length === 0) return null;

  return (
    <div className="quest-notices">
      {showing.map((one) => (
        <div className={`quest-notice ${one.kind}`} key={`${one.id}-${one.at}`} role="status">
          <button
            type="button"
            className="quest-notice-close"
            title="Close this"
            onClick={() => onDismiss(one.at)}
          >
            ✕
          </button>

          <small>{one.kind === 'started' ? 'Quest begun' : 'Quest complete'}</small>
          <b>{one.name}</b>
          {one.about ? <span>{one.about}</span> : null}

          {one.rewards && one.rewards.length > 0 ? (
            <ul className="quest-notice-rewards">
              {one.rewards.map((reward) => (
                <li key={`${reward.text}-${reward.itemId}`}>
                  {reward.quantity > 1 ? `${reward.quantity} x ` : ''}
                  {reward.text}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="quest-notice-acts">
            {one.kind === 'started' ? (
              <button type="button" onClick={() => onFollow(following === one.id ? null : one.id)}>
                {following === one.id ? 'Stop following' : 'Follow it'}
              </button>
            ) : null}
            <button type="button" onClick={() => onOpen(one.id)}>
              Open the journal
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
